// <s-faq-page>: FAQ page helpers around the [data-faq-item] <details> list (open/close animation lives in s-faq.js).
// - [data-faq-search] filters questions live; [data-faq-clear] clears it; [data-faq-count] shows "n questions" / "n of m".
// - [data-faq-toggle-all] expands or collapses every visible question.
// - [data-faq-empty] appears when nothing matches.
// - A #hash matching a question id opens and scrolls to it; [data-faq-copy] copies that question's link.
// Labels come from data-* attributes written by Liquid; __COUNT__ / __TOTAL__ are replaced at runtime.
class FaqPage extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const items = [...this.querySelectorAll('details[data-faq-item]')];
    const search = this.querySelector('[data-faq-search]');
    const clear = this.querySelector('[data-faq-clear]');
    const count = this.querySelector('[data-faq-count]');
    const empty = this.querySelector('[data-faq-empty]');
    const toggleAll = this.querySelector('[data-faq-toggle-all]');
    const total = items.length;

    const visibleItems = () => items.filter((item) => !item.hidden);
    const isOpen = (item) => item.hasAttribute('data-expanded');

    const updateToggleLabel = () => {
      if (!toggleAll) return;
      const visible = visibleItems();
      const allOpen = visible.length > 0 && visible.every(isOpen);
      toggleAll.textContent = allOpen ? this.dataset.labelCollapse : this.dataset.labelExpand;
      toggleAll.setAttribute('aria-pressed', String(allOpen));
      toggleAll.hidden = visible.length === 0;
    };

    const filter = () => {
      const terms = (search?.value || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
      let shown = 0;
      items.forEach((item) => {
        const text = item.dataset.faqText || '';
        const match = terms.every((term) => text.includes(term));
        item.hidden = !match;
        if (match) shown += 1;
      });
      if (count) {
        const template = terms.length ? this.dataset.labelResults : this.dataset.labelCount;
        count.textContent = template.replace('__COUNT__', shown).replace('__TOTAL__', total);
      }
      if (empty) empty.hidden = shown > 0;
      if (clear) clear.hidden = terms.length === 0;
      updateToggleLabel();
    };

    search?.addEventListener('input', filter, { signal });
    search?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && search.value) { search.value = ''; filter(); }
    }, { signal });
    clear?.addEventListener('click', () => { search.value = ''; filter(); search.focus(); }, { signal });

    toggleAll?.addEventListener('click', () => {
      const visible = visibleItems();
      const open = !visible.every(isOpen);
      visible.forEach((item) => {
        if (isOpen(item) !== open) item.querySelector('summary')?.click();
      });
      updateToggleLabel();
    }, { signal });

    // s-faq.js flips [data-expanded] on click; refresh the toggle label after it runs.
    items.forEach((item) => {
      item.querySelector('summary')?.addEventListener('click', () => requestAnimationFrame(updateToggleLabel), { signal });
    });

    this.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-faq-copy]');
      if (!button) return;
      const item = button.closest('details[data-faq-item]');
      if (!item) return;
      const url = new URL(window.location.href);
      url.hash = item.id;
      history.replaceState(null, '', url);
      try { await navigator.clipboard.writeText(url.toString()); } catch (error) { return; }
      const label = button.querySelector('[data-faq-copy-label]');
      if (!label) return;
      const original = label.textContent;
      label.textContent = this.dataset.labelCopied;
      setTimeout(() => { label.textContent = original; }, 1800);
    }, { signal });

    const openFromHash = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id) return;
      const item = items.find((candidate) => candidate.id === id);
      if (!item) return;
      if (!isOpen(item)) item.querySelector('summary')?.click();
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      requestAnimationFrame(() => item.scrollIntoView({ behavior, block: 'start' }));
    };
    window.addEventListener('hashchange', openFromHash, { signal });

    filter();
    // Wait a frame so s-faq.js has wired the summaries before a hash opens one.
    requestAnimationFrame(openFromHash);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }
}

if (!customElements.get('s-faq-page')) customElements.define('s-faq-page', FaqPage);
